import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { Icon3DOr } from '@/components/icons3d/Icon3D'
import { LUCIDE_TO_3D } from '@/components/icons3d/icons3d'
import { useSkin } from '@/theme/skin'

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface'

const PRIMARY = 'bg-brand text-brand-fg hover:bg-brand-hover active:bg-brand-active'
/* Apple-like polish (F-7 addendum): control default is 40px, `sm` 32px. */
const MD = 'h-10 px-4 text-body'

/* Top-level actions share the tenant's primary color. The alias keeps
 * existing call sites stable while page and form actions use one vocabulary. */
const CHROME = PRIMARY

/**
 * Button class recipe. `default` is an accepted alias of `primary` (variant)
 * and of `md` (size) for the alias release, so un-swept call sites keep their
 * current look; both aliases go in SW-6.
 */
export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control font-medium transition-colors duration-150 ease-out ' +
    '[&_svg]:size-4 [&_svg]:shrink-0 disabled:pointer-events-none disabled:opacity-50 touch-manipulation max-sm:min-h-11 max-sm:min-w-11 [@media(pointer:coarse)]:min-h-11 [@media(pointer:coarse)]:min-w-11 ' +
    FOCUS_RING,
  {
    variants: {
      variant: {
        primary: PRIMARY,
        default: PRIMARY,
        secondary: 'bg-surface-2 text-ink hover:bg-line',
        outline: 'border border-line-2 bg-surface text-ink hover:bg-accent',
        ghost: 'text-ink hover:bg-accent',
        destructive: 'bg-danger-solid text-white hover:bg-danger-hover',
        link: 'h-auto p-0 text-brand underline-offset-4 hover:underline',
        chrome: CHROME,
      },
      size: {
        sm: 'h-8 px-3 text-label',
        md: MD,
        default: MD,
        lg: 'h-11 px-5 text-body',
        // F-10 review round (finding 7): icon matches the 40px default
        // control height (was 36px, a size no other control uses - it sat
        // 4px short next to an Input/Select/md Button in the same row).
        // icon-sm keeps the previous 32px for denser contexts (a card
        // header's overflow trigger); several callers were already faking
        // it with `size="icon" className="h-8 w-8"` before this existed.
        icon: 'h-10 w-10 p-0',
        'icon-sm': 'h-8 w-8 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
)

/**
 * Button primitive.
 *
 * Loading is expressed by the caller (`disabled` plus a `Loader2` child), not
 * by a prop, so a button never owns a spinner the caller cannot place.
 *
 * @param {Object} props
 * @param {'primary'|'secondary'|'outline'|'ghost'|'destructive'|'link'|'chrome'|'default'} [props.variant='primary']
 * @param {'sm'|'md'|'lg'|'icon'|'icon-sm'|'default'} [props.size='md']
 * @param {boolean} [props.asChild=false] - Render the single child instead of a
 *   `<button>`, keeping the classes (for `<Button asChild><Link/></Button>`)
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
/** Standard, subtle and secondary buttons with a label: their leading Lucide icon goes 3D (Fluent 3D skin). */
const SUBTLE_VARIANTS = new Set(['outline', 'secondary', 'ghost'])

function ButtonIcons3D({ children }) {
  const skin = useSkin()
  // Only buttons with a visible label: icon-only buttons (row actions in a
  // table, say) repeat down a list and stay flat glyphs.
  const hasLabel = React.Children.toArray(children).some((child) => typeof child === 'string' || typeof child === 'number' || (React.isValidElement(child) && !LUCIDE_TO_3D.has(child.type) && typeof child.type === 'string'))
  if (skin !== 'fluent3d' || !hasLabel) return children
  return React.Children.map(children, (child) => {
    const name = React.isValidElement(child) ? LUCIDE_TO_3D.get(child.type) : null
    // A plus stays a glyph on buttons: "+ Add …" repeats down lists, where a row of 3D plus signs is noise.
    return name && name !== 'plus' ? <Icon3DOr name={name} size={18} className="-my-1">{child}</Icon3DOr> : child
  })
}

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, type, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    // A <button> defaults to type="submit", which turns every button inside a
    // form into an accidental submit; a rendered child (a link, say) must not
    // be given a type at all.
    const resolvedType = type ?? (asChild ? undefined : 'button')
    const isSubtleText = !asChild && SUBTLE_VARIANTS.has(variant) && !String(size || '').startsWith('icon')
    return (
      <Comp
        ref={ref}
        data-variant={variant || 'primary'}
        data-size={size || 'md'}
        className={cn(buttonVariants({ variant, size }), className)}
        {...(resolvedType ? { type: resolvedType } : {})}
        {...props}
      >
        {isSubtleText ? <ButtonIcons3D>{children}</ButtonIcons3D> : children}
      </Comp>
    )
  }
)

Button.displayName = 'Button'

export { Button }
